/**
 * @class
 * @extends Netx.Model
 * @see Netx.Collections.StorageLocations
 */
import Base from '@netx/core/lib/netx-model';

class StorageLocation extends Base {
	/**
	 * @type {Netx.Rpc}
	 * @private
	 */
	get rpc() {
		return true;
	}

	/**
	 * Set the attribute backbone will look to as the id
	 * @type {string}
	 */
	get idAttribute() {
		return 'locationId';
	}

	/**
	 * @property {object}  defaults                     - The default attribute values
	 * @property {number}  defaults.active              - The default system location active (pseudo boolean 0/1)
	 * @property {string}  defaults.basePath            - The default system location base path
	 * @property {number}  defaults.capacitySize        - The defualt system location capacity size
	 * @property {string}  defaults.creationdate        - The defualt system location creation date
	 * @property {number}  defaults.currentSize         - The defualt system location current size
	 * @property {number}  defaults.defaultLocation     - The defualt system location default (pseudo boolean 0/1)
	 * @property {string}  defaults.key1                - The default system location key1
	 * @property {string}  defaults.key2                - The default system location key2
	 * @property {string}  defaults.locationTypeName    - The defualt system location type name
	 * @property {number}  defaults.locationTypeStatus  - The defualt system location type status
	 * @property {number}  defaults.locationTypeId      - The default system location type id - ( File url - 1, HTTP URL - 3, offline - 8, Amazon - 9, Microsoft - 10, box - 101, Not implemented [database - 2, dvd - 4, cd - 5, jazz - 6, zip - 7])
	 * @property {number}  defaults.locationId          - The defualt system location id
	 * @property {number}  defaults.members             - The defualt system location members
	 * @property {string}  defaults.name                - The defualt system location name
	 * @property {string}  defaults.notes               - The defualt system location notes
	 * @property {number}  defaults.sync                - The defualt system location sync (pseudo boolean 0/1)
	 * @property {string}  defaults.userEmail           - The defualt system location user email
	 * @property {number}  defaults.userid              - The defualt system location user id
	 */
	get defaults() {
		return {
			active: 0,
			basePath: '',
			capacitySize: 0,
			creationdate: '',
			currentSize: 0,
			defaultLocation: 0,
			key1: '',
			key2: '',
			locationTypeName: '',
			locationTypeStatus: 0,
			locationTypeId: 0,
			// locationId         : 0,
			members: 0,
			name: '',
			notes: '',
			sync: 0,
			userEmail: '',
			userid: 0,
		};
	}

	/** @type {array} */
	get clientAttrs() {
		return ['label', 'locationTypeLabel'];
	}

	/**
	 * RPC methods:
	 * @rpcs
	 */
	get methods() {
		return {
			/**
			 * @method
			 * @param {object} opts Request options
			 */
			read(opts) {
				return ['getLocationObjects', [this.id], false];
			},
			/**
			 * @method
			 * @param {object} opts Request options
			 */
			create(opts) {
				return ['setLocationObject', this.self()];
			},
			/**
			 * @method
			 * @param {object} opts Request options
			 */
			update(opts) {
				return ['setLocationObject', this.self()];
			},
			/** @method */
			remove: ['deleteLocation', 'locationId'],
		};
	}

	/**
	 * Parsers for this model
	 * @type {object}
	 */
	get parsers() {
		return {
			/**
			 * @function
			 * @param {array} data Data
			 * @param {object} opts Request options
			 */
			read(data, opts) {
				if (!data.length) return null;

				data = data[0];
				return _.extend(data, {
					label: i18n.t('i18n.' + _.toCamelCase(data.name), {
						defaultValue: data.name,
					}),
					locationTypeLabel: i18n.t(
						'i18n.' + _.toCamelCase(data.locationTypeName),
						{defaultValue: data.locationTypeName},
					),
				});
			},
		};
	}

	/** @override */
	constructor(attrs, opts) {
		super(attrs, opts);

		// Hey, listen!
		this.listenTo(
			this,
			'change:locationTypeName',
			(model, locationTypeName) => {
				this.set(
					'locationTypeLabel',
					i18n.t('i18n.' + _.toCamelCase(locationTypeName), {
						defaultValue: locationTypeName,
					}),
				);
			},
		).listenTo(this, 'change:name', (model, name) => {
			this.set(
				'label',
				i18n.t('i18n.' + _.toCamelCase(name), {defaultValue: name}),
			);
		});
	}

	/**
	 * @param {object} data Data
	 * @param {object} opts Options
	 */
	parseAndSave(data, opts) {
		// Collect needed data
		const updatedData = _.extend(
			_.pick.apply(_, [data].concat(_.keys(this.constructor.GetDefaults()))),
			{
				active: data.active || data.defaultLocation ? 1 : 0,
				defaultLocation: data.defaultLocation ? 1 : 0,
				sync: data.sync ? 1 : 0,
				locationTypeId: parseInt(data.locationTypeId),
			},
		);

		// Save changes
		return this.save(updatedData, _.extend({wait: true}, opts));
	}

	/**
	 * @method
	 * @param {boolean} active Active
	 * @param {object} opts Options
	 */
	setActive(active, opts) {
		return this.save(
			{
				sync: active ? 1 : 0,
			},
			_.extend({wait: true}, opts),
		);
	}

	/**
	 * @method
	 * @param {object} opts Options
	 */
	setAsDefault(opts) {
		return this.save(
			{
				active: 1,
				defaultLocation: 1,
			},
			_.extend({wait: true}, opts),
		);
	}

	/**
	 * @method
	 * @param {boolean} sync Sync
	 * @param {object} opts Options
	 */
	setSync(sync, opts) {
		return this.save(
			{
				sync: sync ? 1 : 0,
			},
			_.extend({wait: true}, opts),
		);
	}

	/** @type {string} */
	static get __name__() {
		return 'StorageLocation';
	}
}

export default StorageLocation;
